import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Users, Calendar, CalendarDays, FileText, DollarSign, Shield, Clock, AlertTriangle,
  ChevronDown, Plus, ExternalLink, Wallet,
  Building, Briefcase, PenTool, ClipboardList, UserCheck, TrendingUp, TrendingDown, FileCheck,
  Search, Mail, Sparkles, Filter
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import DashboardLayout from '@/components/DashboardLayoutV2';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAvailableModules } from '@/hooks/useAvailableModules';
import { usePermissions } from '@/contexts/PermissionContext';
import { getVisibleWidgets, QUICK_ACTIONS as RBAC_QUICK_ACTIONS } from '@/utils/dashboardWidgetRegistry';
import { getWidgetActionCount, WIDGET_ACTION_CENTER_MODULE } from '@/utils/widgetActionFilters';
import { getWidgetActionFooterLabel } from '@/utils/widgetActionFooterLabels';
import { incidentBreakdownForLegend } from '@/utils/incidentChartRules';
import { DashboardActionCenter } from '@/components/dashboard/DashboardActionCenter';
import { HeroComplianceProgressCard } from '@/components/dashboard/HeroComplianceProgressCard';
import { WidgetActionBadge, WidgetActionFooter } from '@/components/dashboard/WidgetActionSignals';
import useActionCenter from '@/hooks/useActionCenter';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import NotificationTrackerWidget from '@/components/widgets/NotificationTrackerWidget';
import { DashboardTileGrid } from '@/components/dashboard/DashboardTileGrid';
import * as SelectPrimitive from "@radix-ui/react-select";
import { cn } from "@/lib/utils";
import { OnboardingHubModal } from '@/components/onboarding-hub/OnboardingHubModal';
import { GettingStartedButton } from '@/components/onboarding-hub/GettingStartedButton';
import { clearPartADeferral, partADeferred } from '@/components/onboarding-hub/partASignOut';
import { partANeedsModal } from '@/hooks/useOnboardingHub';

// --- CUSTOM COMPONENTS ---

// Custom Select Trigger without Chevron
const IconSelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-10 w-10 items-center justify-center rounded-full border border-input bg-white shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
      className
    )}
    {...props}>
    {children}
  </SelectPrimitive.Trigger>
));
IconSelectTrigger.displayName = "IconSelectTrigger";

// --- CONSTANTS & CONFIG ---
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  emerald: { bg: 'bg-emerald-500', text: 'text-emerald-600', light: 'bg-emerald-50 text-emerald-700', hex: '#10b981' },
  blue: { bg: 'bg-blue-500', text: 'text-blue-600', light: 'bg-blue-50 text-blue-700', hex: '#3b82f6' },
  violet: { bg: 'bg-violet-500', text: 'text-violet-600', light: 'bg-violet-50 text-violet-700', hex: '#8b5cf6' },
  amber: { bg: 'bg-amber-500', text: 'text-amber-600', light: 'bg-amber-50 text-amber-700', hex: '#f59e0b' },
  rose: { bg: 'bg-rose-500', text: 'text-rose-600', light: 'bg-rose-50 text-rose-700', hex: '#f43f5e' },
  cyan: { bg: 'bg-cyan-500', text: 'text-cyan-600', light: 'bg-cyan-50 text-cyan-700', hex: '#06b6d4' },
  slate: { bg: 'bg-slate-500', text: 'text-slate-600', light: 'bg-slate-50 text-slate-700', hex: '#64748b' },
};

const CHART_COLORS = ['#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#64748b'];

const PERIODS = [
  { value: 'week', label: 'This Week' }, { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' }, { value: 'year', label: 'This Year' },
  { value: 'all', label: 'All Time' },
];

// Quick Actions Configuration — permissions resolved via dashboardWidgetRegistry
const QUICK_ACTION_ICON_MAP = {
  Calendar, Users, DollarSign, Building, Shield, Briefcase,
};

const QUICK_ACTIONS = RBAC_QUICK_ACTIONS.map((qa) => ({
  ...qa,
  icon: QUICK_ACTION_ICON_MAP[qa.icon] || Calendar,
}));

// --- MODERN WIDGET COMPONENTS ---

const MiniSparkline = ({ data, color }) => {
  if (!data || data.length < 2) return null;
  const chartData = data.map((val, i) => ({ i, val }));
  return (
    <div className="h-8 w-24">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id={`gradient-${color}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={color} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="val" stroke={color} fillOpacity={1} fill={`url(#gradient-${color})`} strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const ActionButton = ({ icon: Icon, label, desc, onClick, colorClass }) => (
  <button 
    onClick={onClick}
    className="group flex flex-col items-start p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-purple-200 dark:hover:border-purple-900 hover:shadow-sm transition-all duration-200 w-full text-left"
  >
    <div className={`p-2 rounded-lg ${colorClass} bg-opacity-10 mb-3 group-hover:scale-110 transition-transform duration-200`}>
      <Icon className={`h-5 w-5 ${colorClass.replace('bg-', 'text-')}`} />
    </div>
    <span className="font-semibold text-sm text-foreground mb-0.5">{label}</span>
    <span className="text-xs text-muted-foreground">{desc}</span>
  </button>
);

// 1. Stat Card (For key metrics)
const ModernStatCard = ({ title, value, subtitle, icon: Icon, theme = 'violet', trend, onClick }) => {
  const colors = COLORS[theme] || COLORS.violet;
  return (
    <Card 
      onClick={onClick}
      className="group relative overflow-hidden border-none shadow-sm bg-white dark:bg-gray-900 hover:shadow-md transition-all duration-200 cursor-pointer h-full"
    >
      <CardContent className="p-5 flex flex-col justify-between h-full">
        <div className="flex justify-between items-start mb-2">
          <div className={`p-2.5 rounded-xl ${colors.light} group-hover:scale-110 transition-transform duration-200`}>
            <Icon className="h-5 w-5" />
          </div>
          {trend && <div className="ml-auto"><MiniSparkline data={trend} color={colors.hex} /></div>}
        </div>
        <div>
          <h3 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50">{value}</h3>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1 opacity-80">{subtitle}</p>}
        </div>
      </CardContent>
    </Card>
  );
};

// 2. Budget Style Widget (Horizontal Bar) - Compact
const BudgetStyleWidget = ({ title, data, icon: Icon, theme = 'blue', onClick, total, actionCount, onViewActions, footerLabel }) => {
  const colors = COLORS[theme] || COLORS.blue;
  
  // Transform and sort data
  const items = Object.entries(data || {})
    .map(([name, value]) => ({ 
      name: name.replace(/_/g, ' '), 
      value: typeof value === 'number' ? value : 0
    }))
    .sort((a,b) => b.value - a.value)
    .slice(0, 3);

  const calcTotal = total || items.reduce((acc, curr) => acc + curr.value, 0) || 1; 

  return (
    <Card onClick={onClick} className="group border-none shadow-sm bg-white dark:bg-gray-900 hover:shadow-md transition-all duration-200 cursor-pointer h-full flex flex-col">
      <CardHeader className="pb-2 pt-4 px-4 flex flex-row items-center justify-between space-y-0 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <CardTitle className="text-base font-semibold text-foreground truncate">{title}</CardTitle>
          <WidgetActionBadge count={actionCount} />
        </div>
        <div className={`p-2 rounded-lg ${colors.light} bg-opacity-20 shrink-0`}>
          <Icon className="h-4 w-4" />
        </div>
      </CardHeader>
      
      <CardContent className="px-4 pb-4 space-y-3 flex-1 flex flex-col">
        {items.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-4">No data available</div>
        ) : (
          items.map((item, idx) => {
            const pct = calcTotal > 0 ? Math.min(Math.round((item.value / calcTotal) * 100), 100) : 0;
            return (
              <div key={idx} className="space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-foreground capitalize">{item.name}</span>
                  <span className="text-sm font-bold text-foreground">
                    {item.value} 
                    <span className="ml-1 text-xs font-normal text-muted-foreground">
                      ({pct}%)
                    </span>
                  </span>
                </div>
                <div className="h-2.5 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all duration-500 ease-out ${colors.bg}`} 
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })
        )}
        
        {items.length > 0 && (
          <div className="pt-1 flex items-start gap-2 text-xs text-muted-foreground">
            <Sparkles className={`h-3.5 w-3.5 shrink-0 ${colors.text} mt-0.5`} />
            <span className="leading-snug truncate">
              <strong>{items[0].name}</strong> is the top category.
            </span>
          </div>
        )}
        <div className="mt-auto">
          <WidgetActionFooter count={actionCount} onViewActions={onViewActions} label={footerLabel} />
        </div>
      </CardContent>
    </Card>
  );
};

// Policy widget — same footprint as standard tiles
const PolicyManagementWidget = ({ policy, widgetActionProps, onNavigate }) => (
  <Card
    onClick={() => onNavigate('/policy')}
    className="group border-none shadow-sm bg-white dark:bg-gray-900 hover:shadow-md transition-all duration-200 cursor-pointer h-full flex flex-col"
    data-testid="policy-dashboard-widget"
  >
    <CardHeader className="pb-2 pt-4 px-4 flex flex-row items-center justify-between space-y-0 shrink-0">
      <div className="flex items-center gap-2 min-w-0">
        <CardTitle className="text-base font-semibold text-foreground truncate">Policy Management</CardTitle>
        <WidgetActionBadge count={widgetActionProps.actionCount} />
      </div>
      <div className="p-2 rounded-lg bg-indigo-50 text-indigo-700 bg-opacity-20 shrink-0">
        <ClipboardList className="h-4 w-4" />
      </div>
    </CardHeader>
    <CardContent className="px-4 pb-4 space-y-2.5">
      <div className="grid grid-cols-3 gap-2">
        <div className="text-center p-2 rounded-lg bg-emerald-50/60">
          <p className="text-lg font-bold text-emerald-700">{policy?.active || 0}</p>
          <p className="text-[10px] text-emerald-600 font-medium">Active</p>
        </div>
        <div className="text-center p-2 rounded-lg bg-slate-50/60">
          <p className="text-lg font-bold text-slate-700">{policy?.draft || 0}</p>
          <p className="text-[10px] text-slate-500 font-medium">Draft</p>
        </div>
        <div className="text-center p-2 rounded-lg bg-amber-50/60">
          <p className="text-lg font-bold text-amber-700">{policy?.overdue_reviews || 0}</p>
          <p className="text-[10px] text-amber-600 font-medium">Overdue</p>
        </div>
      </div>
      <div className="space-y-1.5">
        {(policy?.pending_approvals || 0) > 0 && (
          <div
            className="flex items-center justify-between p-2 rounded-lg bg-blue-50/60 border border-blue-100"
            onClick={(e) => { e.stopPropagation(); onNavigate('/policy'); }}
          >
            <span className="text-xs font-medium text-blue-800">Pending Approvals</span>
            <span className="text-sm font-bold text-blue-700">{policy?.pending_approvals}</span>
          </div>
        )}
        {(policy?.review_requests || 0) > 0 && (
          <div className="flex items-center justify-between p-2 rounded-lg bg-violet-50/60 border border-violet-100">
            <span className="text-xs font-medium text-violet-800">Review Requests</span>
            <span className="text-sm font-bold text-violet-700">{policy?.review_requests}</span>
          </div>
        )}
        <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50/60">
          <span className="text-xs text-muted-foreground">Acknowledgment Rate</span>
          <span className={`text-sm font-bold ${(policy?.pct || 0) >= 90 ? 'text-emerald-700' : (policy?.pct || 0) >= 60 ? 'text-amber-700' : 'text-red-700'}`}>
            {policy?.pct || 0}%
          </span>
        </div>
      </div>
      <WidgetActionFooter
        count={widgetActionProps.actionCount}
        onViewActions={widgetActionProps.onViewActions}
        label={widgetActionProps.footerLabel}
      />
    </CardContent>
  </Card>
);

// 3. Approval Status Widget (Columnar) - Fixed & Compact
const ApprovalStatusWidget = ({ title, data, total, icon: Icon, theme = 'amber', onClick, trend, actionCount, onViewActions, footerLabel }) => {
  const colors = COLORS[theme] || COLORS.amber;
  
  const renderTrend = (val) => {
    if (val === undefined || val === null) return <span className="h-4 block"></span>; 
    const isUp = val > 0;
    const isDown = val < 0;
    return (
      <span className={`text-[10px] font-medium flex items-center justify-center mt-1 ${isUp ? 'text-emerald-600' : isDown ? 'text-rose-600' : 'text-gray-500'}`}>
        {isUp ? '+' : ''}{val} vs last
      </span>
    );
  };

  let trendVal = 0;
  if (Array.isArray(trend) && trend.length >= 2) {
    trendVal = trend[trend.length - 1] - trend[trend.length - 2];
  } else if (typeof trend === 'number') {
    trendVal = trend;
  }

  const columns = [
    { label: 'Requested', value: total || 0, trend: trendVal, color: 'text-foreground' },
    { label: 'Approved', value: data?.approved || 0, color: 'text-emerald-600' }, 
    { label: 'Rejected', value: data?.rejected || 0, color: 'text-rose-600' },    
    { label: 'Pending', value: data?.pending || 0, color: 'text-amber-600' }      
  ];

  return (
    <Card onClick={onClick} className="group border-none shadow-sm bg-white dark:bg-gray-900 hover:shadow-md transition-all duration-200 cursor-pointer h-full flex flex-col">
      <CardHeader className="pb-3 pt-4 px-4 flex flex-row items-center justify-between space-y-0 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <CardTitle className="text-base font-semibold text-foreground truncate">{title}</CardTitle>
          <WidgetActionBadge count={actionCount} />
        </div>
        <div className={`p-2 rounded-lg ${colors.light} bg-opacity-20 shrink-0`}>
          <Icon className="h-4 w-4" />
        </div>
      </CardHeader>
      
      <CardContent className="px-4 pb-4 flex-1 flex flex-col">
        <div className="grid grid-cols-4 divide-x divide-gray-100 dark:divide-gray-800 flex-1">
          {columns.map((col, idx) => (
            <div key={idx} className="flex flex-col items-center px-1">
              <span className="text-xs text-muted-foreground font-medium mb-2 truncate w-full text-center">{col.label}</span>
              <span className={`text-xl sm:text-2xl font-bold leading-none ${col.color}`}>{col.value}</span>
              {col.trend !== undefined && renderTrend(col.trend)}
            </div>
          ))}
        </div>
        <WidgetActionFooter count={actionCount} onViewActions={onViewActions} label={footerLabel} />
      </CardContent>
    </Card>
  );
};

// 4. Modern Donut Widget - Compact
const ModernDonutWidget = ({ title, data, total, icon: Icon, theme = 'emerald', onClick, subTitle, actionCount, onViewActions, footerLabel }) => {
  const colors = COLORS[theme] || COLORS.emerald;
  const chartData = Object.entries(data || {}).map(([name, value]) => ({ 
    name: name.replace(/_/g, ' '), 
    value: typeof value === 'number' ? value : 0
  }));
  const hasBreakdown = chartData.some(d => d.value > 0);
  const hasData = hasBreakdown || (total != null && total > 0);
  const displayData = hasBreakdown ? chartData : [{ name: 'Open', value: total || 0 }];

  return (
    <Card onClick={onClick} className="group border-none shadow-sm bg-white dark:bg-gray-900 hover:shadow-md transition-all duration-200 cursor-pointer h-full flex flex-col">
      <CardHeader className="pb-2 pt-4 px-4 flex flex-row items-center justify-between space-y-0 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <CardTitle className="text-base font-semibold text-foreground truncate">{title}</CardTitle>
          <WidgetActionBadge count={actionCount} />
        </div>
        <div className={`p-2 rounded-lg ${colors.light} bg-opacity-20 shrink-0`}>
          <Icon className="h-4 w-4" />
        </div>
      </CardHeader>
      
      <CardContent className="px-4 pb-4 flex-1 flex flex-col">
        {!hasData ? (
           <div className="text-center text-sm text-muted-foreground py-6 flex-1 flex items-center justify-center">No data available</div>
        ) : (
          <div className="flex items-center gap-4 flex-1">
            <div className="relative h-24 w-24 flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', padding: '4px 8px' }}
                    itemStyle={{ fontSize: '12px', fontWeight: '500', color: '#111827', padding: 0 }}
                    formatter={(value) => [value, '']} 
                  />
                  <Pie
                    data={displayData}
                    cx="50%"
                    cy="50%"
                    innerRadius={35}
                    outerRadius={45}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="none"
                  >
                    {displayData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-sm font-bold">{total ?? displayData.reduce((s, d) => s + d.value, 0)}</span>
                {subTitle && <span className="text-[9px] text-muted-foreground uppercase">{subTitle}</span>}
              </div>
            </div>
            
            <div className="flex-1 space-y-1.5 min-w-0">
              {displayData.slice(0, 3).map((item, index) => (
                <div key={index} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }} />
                    <span className="truncate text-gray-700 dark:text-gray-300 capitalize font-medium">{item.name}</span>
                  </div>
                  <span className="font-bold">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <WidgetActionFooter count={actionCount} onViewActions={onViewActions} label={footerLabel} />
      </CardContent>
    </Card>
  );
};

// 5. Hero — greeting, KPI tiles, toolbar, compliance-only card
const ComplianceHeroWidget = ({ user, complianceData, stats, headerActions, showCompliance = true, onComplianceClick }) => {
  const percent = complianceData?.pct || 0;
  const greeting = `Hello, ${user?.first_name || 'Team'}`;
  const dateStr = new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <Card className="border-none shadow-sm bg-[#fcf8e8] dark:bg-amber-950/20 overflow-visible relative mb-8" data-testid="compliance-hero-widget">
      <CardContent className="p-6 lg:p-8 relative z-10 space-y-6">
        {/* Row 1: Greeting + toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-heading font-medium text-gray-900 dark:text-gray-50 mb-1 tracking-tight">
              {greeting}
            </h1>
            <p className="text-sm text-muted-foreground">{dateStr}</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">{headerActions}</div>
        </div>

        {/* Row 2: KPI tiles + compliance card */}
        <div className="flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-8">
          {stats.length > 0 && (
            <div className="flex-1 grid grid-cols-2 xl:grid-cols-4 gap-3">
              {stats.map((stat, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-4 rounded-xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm"
                >
                  <div className="p-2 rounded-lg bg-white/80 dark:bg-black/20 border border-black/5 shrink-0">
                    <stat.icon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xl lg:text-2xl font-bold leading-none text-gray-900 dark:text-gray-100 tabular-nums">{stat.value}</p>
                    <p className="text-xs text-muted-foreground mt-1 font-medium truncate">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showCompliance && (
            <HeroComplianceProgressCard compliancePct={percent} onClick={onComplianceClick} />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// --- MAIN PAGE ---

const CompanyDashboard = ({ user, onLogout, onUserRefresh }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [heroMetrics, setHeroMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [hubOpen, setHubOpen] = useState(false);

  const { permissions, isAdmin: contextIsAdmin } = usePermissions();
  const isCompanyAdmin = user?.role === 'company_admin';
  const isSuperadmin = user?.role === 'superadmin';

  // Match DashboardLayoutV2: fall back to session permissions when context refresh is empty
  const effectivePermissions = useMemo(() => {
    if (permissions?.length > 0) return permissions;
    if (Array.isArray(user?.permissions) && user.permissions.length > 0) return user.permissions;
    return permissions || [];
  }, [permissions, user?.permissions]);

  const effectiveIsAdmin = contextIsAdmin || isCompanyAdmin || isSuperadmin;

  const effectiveHasAnyPermission = useCallback(
    (...permissionKeys) => {
      if (effectiveIsAdmin) return true;
      return permissionKeys.some((key) => effectivePermissions.includes(key));
    },
    [effectiveIsAdmin, effectivePermissions]
  );

  const effectiveHasPermission = useCallback(
    (permissionKey) => {
      if (effectiveIsAdmin) return true;
      return effectivePermissions.includes(permissionKey);
    },
    [effectiveIsAdmin, effectivePermissions]
  );

  // Module + RBAC Gating
  const { modules: availableModules, loading: modulesLoading } = useAvailableModules();

  const visibleWidgets = useMemo(() => {
    const mods = availableModules || [];
    // Only wait when modules are still loading and none are cached yet
    if (modulesLoading && mods.length === 0) return [];
    return getVisibleWidgets({
      availableModules: mods,
      hasAnyPermission: effectiveHasAnyPermission,
      isAdmin: effectiveIsAdmin,
    });
  }, [availableModules, modulesLoading, effectiveHasAnyPermission, effectiveIsAdmin]);

  // Auth Headers
  const token = localStorage.getItem('access_token');
  const hdrs = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Fetch Real Data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cr, hr] = await Promise.all([
        fetch(`${API}/dashboard/charts?period=${period}`, {headers: hdrs}),
        fetch(`${API}/dashboard/hero-metrics?period=${period}`, {headers: hdrs}),
      ]);
      if (cr.ok) setData(await cr.json());
      if (hr.ok) setHeroMetrics(await hr.json());
    } catch (e) { 
      console.error("Dashboard fetch error:", e);
    } finally { 
      setLoading(false); 
    }
  }, [period, hdrs]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Part A ("Set up your company") is a blocker for company_admin until completed.
  useEffect(() => {
    if (!isCompanyAdmin) return;
    if (searchParams.get('onboarding') === '1') {
      const next = new URLSearchParams(searchParams);
      next.delete('onboarding');
      setSearchParams(next, { replace: true });
      // Fresh signup shows the setup modal again, even if this tab hid it earlier.
      clearPartADeferral();
      setHubOpen(true);
      return;
    }
    if (partADeferred()) return;
    if (partANeedsModal(user)) {
      setHubOpen(true);
      return;
    }
    // Session may predate setup_hub; confirm with the server once.
    if (user?.setup_hub === undefined) {
      axios.get(`${API}/onboarding-hub/progress`, { headers: hdrs })
        .then((res) => {
          if (partADeferred()) return;
          if (res.data && res.data.part_a_completed === false) setHubOpen(true);
        })
        .catch(() => {});
    }
  }, [user, searchParams, isCompanyAdmin, setSearchParams, hdrs, API]);

  const { actionItems, loading: actionLoading, refresh: refreshActions, openActionCenter } = useActionCenter();

  const widgetAction = useCallback(
    (widgetId) => ({
      actionCount: getWidgetActionCount(actionItems, widgetId),
      footerLabel: getWidgetActionFooterLabel(actionItems, widgetId),
      onViewActions: () => openActionCenter(WIDGET_ACTION_CENTER_MODULE[widgetId] || 'all'),
    }),
    [actionItems, openActionCenter]
  );

  const w = data?.widgets || {};
  const hm = heroMetrics?.metrics || {};

  // Helper to safely render widgets if module + RBAC enabled
  const has = (id) => visibleWidgets.includes(id);

  // Helper to extract delta from trend array
  const getTrendDelta = (trendArr) => {
    if (Array.isArray(trendArr) && trendArr.length >= 2) {
      return trendArr[trendArr.length - 1] - trendArr[trendArr.length - 2];
    }
    return 0; // Default to 0 if no trend data
  };

  // Helper to construct Breakdown Data manually if API doesn't provide it
  const getQuotesData = () => {
    if (w.quotes?.breakdown) return w.quotes.breakdown;
    return {
      Draft: w.quotes?.draft || 0,
      Sent: w.quotes?.sent || 0,
      Accepted: w.quotes?.accepted || 0,
      Converted: w.quotes?.converted || 0
    };
  };

  const getServiceAgreementsData = () => {
    if (w.service_agreements?.breakdown) return w.service_agreements.breakdown;
    return {
      Active: w.service_agreements?.active || 0,
      Draft: w.service_agreements?.draft || 0,
      Pending: w.service_agreements?.pending || 0
    };
  };

  const getEsignaturesData = () => {
    if (w.esignatures?.breakdown) return w.esignatures.breakdown;
    return {
      Draft: w.esignatures?.draft || 0,
      Pending: w.esignatures?.pending || 0,
      Completed: w.esignatures?.completed || 0
    };
  };

  const getComplianceDocumentsData = () => {
    const pending = w.compliance?.actions?.pending_review ?? 0;
    const compliant = w.compliance?.compliant ?? 0;
    const total = w.compliance?.total ?? 0;
    const nonCompliant = Math.max(0, total - compliant - pending);
    return {
      'Pending review': pending,
      Compliant: compliant,
      'Needs attention': nonCompliant,
    };
  };

  const getInvoiceFollowupsData = () => {
    const breakdown = w.invoices?.breakdown || {};
    return {
      Outstanding: breakdown.outstanding ? Math.round(breakdown.outstanding) : 0,
      Paid: breakdown.paid ? Math.round(breakdown.paid) : 0,
      'Written off': breakdown.written_off ? Math.round(breakdown.written_off) : 0,
    };
  };

  // Prepare Stats for Hero Header (scoped via hero-metrics API)
  const heroStats = [
    hm.employees?.visible && { label: 'Employees', value: hm.employees.total || 0, icon: Users },
    hm.shifts?.visible && { label: 'Shifts', value: hm.shifts.total || 0, icon: Calendar },
    hm.invoiced?.visible && { label: 'Invoiced', value: `$${((hm.invoiced.total||0)/1000).toFixed(1)}k`, icon: DollarSign },
    hm.budget_used?.visible && { label: 'Budget Used', value: `${hm.budget_used.utilization_pct || 0}%`, icon: Wallet },
  ].filter(Boolean);

  const showComplianceGauge = Boolean(hm.compliance?.visible);
  const complianceData = hm.compliance || { pct: 0 };

  const showNotificationTracker =
    has('incidents') &&
    effectiveHasAnyPermission('incident.report.view_own', 'incident.report.view_team', 'incident.report.view_all');

  const dashboardTiles = useMemo(() => {
    const tiles = [];

    if (showNotificationTracker) {
      tiles.push({
        id: 'notification_tracker',
        render: () => <NotificationTrackerWidget />,
      });
    }

    if (has('incidents')) {
      tiles.push({
        id: 'incidents',
        render: () => (
          <ModernDonutWidget
            title="Incident Reports"
            data={incidentBreakdownForLegend(w.incidents?.breakdown)}
            total={w.incidents?.open ?? 0}
            subTitle="Open"
            icon={AlertTriangle}
            theme="rose"
            onClick={() => navigate('/incidents')}
            {...widgetAction('incidents')}
            actionCount={w.incidents?.open ?? 0}
            footerLabel={`${w.incidents?.open ?? 0} open`}
          />
        ),
      });
    }

    if (has('compliance_documents')) {
      tiles.push({
        id: 'compliance_documents',
        render: () => (
          <BudgetStyleWidget
            title="Compliance Documents"
            data={getComplianceDocumentsData()}
            total={w.compliance?.total}
            icon={Shield}
            theme="amber"
            onClick={() => navigate('/company/compliance')}
            {...widgetAction('compliance_documents')}
          />
        ),
      });
    }

    if (has('policy')) {
      tiles.push({
        id: 'policy',
        render: () => (
          <PolicyManagementWidget
            policy={w.policy}
            widgetActionProps={widgetAction('policy')}
            onNavigate={navigate}
          />
        ),
      });
    }

    if (has('invoice_followups')) {
      tiles.push({
        id: 'invoice_followups',
        render: () => (
          <BudgetStyleWidget
            title="Invoice Follow-ups"
            data={getInvoiceFollowupsData()}
            total={w.invoices?.total}
            icon={DollarSign}
            theme="emerald"
            onClick={() => navigate('/invoices')}
            {...widgetAction('invoice_followups')}
          />
        ),
      });
    }

    if (has('quotes')) {
      tiles.push({
        id: 'quotes',
        render: () => (
          <BudgetStyleWidget
            title="Quotes Pipeline"
            data={getQuotesData()}
            total={w.quotes?.total}
            icon={FileText}
            theme="cyan"
            onClick={() => navigate('/ndis/quotes')}
            {...widgetAction('quotes')}
          />
        ),
      });
    }

    if (has('service_agreements')) {
      tiles.push({
        id: 'service_agreements',
        render: () => (
          <BudgetStyleWidget
            title="Service Agreements"
            data={getServiceAgreementsData()}
            total={w.service_agreements?.total}
            icon={FileCheck}
            theme="blue"
            onClick={() => navigate('/service-agreements-v2')}
            {...widgetAction('service_agreements')}
          />
        ),
      });
    }

    if (has('esignatures')) {
      tiles.push({
        id: 'esignatures',
        render: () => (
          <BudgetStyleWidget
            title="E-Signatures"
            data={getEsignaturesData()}
            total={w.esignatures?.total}
            icon={PenTool}
            theme="rose"
            onClick={() => navigate('/esignature')}
            {...widgetAction('esignatures')}
          />
        ),
      });
    }

    if (has('jobs')) {
      tiles.push({
        id: 'jobs',
        render: () => (
          <BudgetStyleWidget
            title="Recruitment Pipeline"
            data={w.jobs?.breakdown}
            total={w.jobs?.total}
            icon={Briefcase}
            theme="violet"
            onClick={() => navigate('/recruitment/jobs')}
            {...widgetAction('jobs')}
          />
        ),
      });
    }

    if (has('reference_checks')) {
      tiles.push({
        id: 'reference_checks',
        render: () => (
          <BudgetStyleWidget
            title="Reference Checks"
            data={w.reference_checks?.breakdown}
            total={w.reference_checks?.total}
            icon={UserCheck}
            theme="violet"
            onClick={() => navigate('/recruitment/reference-check')}
            {...widgetAction('reference_checks')}
          />
        ),
      });
    }

    if (has('leave')) {
      tiles.push({
        id: 'leave',
        render: () => (
          <ApprovalStatusWidget
            title="Leave Requests"
            data={w.leave?.breakdown}
            total={w.leave?.total}
            trend={getTrendDelta(w.leave?.trend)}
            icon={Clock}
            theme="amber"
            onClick={() => navigate('/leave/approve')}
            {...widgetAction('leave')}
          />
        ),
      });
    }

    return tiles;
  }, [showNotificationTracker, visibleWidgets, w, navigate, widgetAction]);

  const visibleQuickActions = QUICK_ACTIONS.filter((qa) => {
    const moduleOk = !qa.module || (availableModules || []).includes(qa.module);
    const permOk = effectiveIsAdmin || !qa.permission || effectiveHasPermission(qa.permission);
    return moduleOk && permOk;
  });

  // Define Header Controls (Round Buttons Style with Select primitive)
  const headerControls = (
    <div className="flex gap-3 items-center">
      <GettingStartedButton user={user} />
      <DashboardActionCenter
        actionItems={actionItems}
        actionLoading={actionLoading}
        onRefresh={refreshActions}
      />
      {/* Period Selector Button */}
      <Select value={period} onValueChange={(v) => setPeriod(v)}>
        <IconSelectTrigger className="w-10 h-10 border-2 border-amber-100 hover:bg-amber-50 hover:border-amber-200 transition-all shadow-sm">
           <Calendar className="h-5 w-5 text-amber-600" />
        </IconSelectTrigger>
        <SelectContent align="end">
          {PERIODS.map((p) => (
            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <DashboardLayout user={user} onLogout={onLogout}>
      <OnboardingHubModal
        open={hubOpen}
        onSignOut={onLogout}
        onFinishLater={() => setHubOpen(false)}
        onCompleted={() => {
          setHubOpen(false);
          onUserRefresh?.();
        }}
      />
      <div className="px-1 sm:px-0" data-testid="company-dashboard">
        
        {loading && !data ? (
           <div className="space-y-6">
             <Skeleton className="h-64 w-full rounded-xl" />
             <div className="grid grid-cols-12 gap-4 auto-rows-[minmax(200px,auto)]">
               {[1, 2, 3, 4, 5, 6].map((i) => (
                 <Skeleton
                   key={i}
                   className={`h-48 rounded-xl ${i === 1 ? 'col-span-12' : i <= 3 ? 'col-span-12 sm:col-span-6 lg:col-span-4' : 'col-span-12 lg:col-span-6'}`}
                 />
               ))}
             </div>
           </div>
        ) : (
          <>
            {/* 1. HERO SECTION (greeting + scoped metrics + optional compliance gauge) */}
            <ComplianceHeroWidget 
              user={user} 
              complianceData={complianceData} 
              stats={heroStats} 
              headerActions={headerControls}
              showCompliance={showComplianceGauge}
              onComplianceClick={() => navigate('/company/compliance')}
            />

            {/* 2. QUICK ACTIONS GRID */}
            {visibleQuickActions.length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-4 px-1">Quick Actions</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {visibleQuickActions.map(qa => (
                  <ActionButton 
                    key={qa.label}
                    icon={qa.icon} 
                    label={qa.label} 
                    desc={qa.desc} 
                    colorClass={qa.color} 
                    onClick={() => navigate(qa.route)}
                  />
                ))}
              </div>
            </div>
            )}

            {/* Unified widget tile board */}
            <DashboardTileGrid tiles={dashboardTiles} className="mb-10" />

          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CompanyDashboard;
